import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './ApiLogs.css';

const ApiLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    log_type: '',
    order_number: '',
    start_date: '',
    end_date: ''
  });

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await api.get(`/admin/api-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
    } catch (err) {
      setError('Failed to load API logs. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleExport = async (format = 'json') => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/admin/api-logs/export/${format}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `order-status-api-logs-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export logs. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="api-logs-page">
          <div className="container">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading API logs...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="api-logs-page">
        <div className="container">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="filters-section">
            <h3>Filters</h3>
            <div className="filters-grid">
              <div className="filter-item">
                <label>Log Type:</label>
                <select
                  value={filters.log_type}
                  onChange={(e) => handleFilterChange('log_type', e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="hdfc_gateway">HDFC Gateway API Response</option>
                  <option value="error">Errors</option>
                </select>
              </div>
              <div className="filter-item">
                <label>Order Number:</label>
                <input
                  type="text"
                  value={filters.order_number}
                  onChange={(e) => handleFilterChange('order_number', e.target.value)}
                  placeholder="Order Number"
                />
              </div>
              <div className="filter-item">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
              </div>
              <div className="filter-item">
                <label>End Date:</label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </div>
            </div>
            <div className="filter-actions">
              <button onClick={fetchLogs} className="btn-apply-filters">
                Apply Filters
              </button>
              <button 
                onClick={() => setFilters({ log_type: '', order_number: '', start_date: '', end_date: '' })}
                className="btn-clear-filters"
              >
                Clear Filters
              </button>
              <button onClick={() => handleExport('json')} className="btn-export">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export as JSON
              </button>
            </div>
          </div>

          {logs.length === 0 ? (
            <div className="empty-state">
              <p>No API logs found{Object.values(filters).some(f => f) ? ' matching the filters' : ''}.</p>
            </div>
          ) : (
            <div className="logs-list">
              {logs.map((log) => (
                <div key={log.id} className={`log-card ${log.log_type === 'hdfc_gateway' ? 'hdfc-gateway-card' : ''}`}>
                  <div className="log-header">
                    <div className="log-meta">
                      <span className="log-type-badge">
                        {log.log_type === 'hdfc_gateway' ? 'HDFC Gateway API Response' : log.log_type}
                      </span>
                      <span className={`status-code status-${Math.floor(log.status_code / 100)}xx`}>
                        {log.status_code}
                      </span>
                      <span className="log-method">{log.method}</span>
                      <span className="log-endpoint">{log.endpoint}</span>
                    </div>
                    <div className="log-date">{formatDate(log.created_at)}</div>
                  </div>
                  
                  {log.log_type !== 'hdfc_gateway' && (
                    <div className="log-details">
                      <div className="detail-row">
                        <span className="detail-label">Order ID:</span>
                        <span className="detail-value">{log.order_id || 'N/A'}</span>
                      </div>
                      {log.order_number && (
                        <div className="detail-row">
                          <span className="detail-label">Order Number:</span>
                          <span className="detail-value">{log.order_number}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="detail-label">Status:</span>
                        <span className="detail-value">{log.status || 'N/A'}</span>
                      </div>
                      {log.payment_status && (
                        <div className="detail-row">
                          <span className="detail-label">Payment Status:</span>
                          <span className="detail-value">{log.payment_status}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="log-data">
                    {log.log_type === 'hdfc_gateway' ? (
                      <div className="data-section hdfc-gateway-response">
                        <h4>HDFC Gateway API Response</h4>
                        <pre className="json-view">
                          {typeof log.response_data === 'string' 
                            ? log.response_data 
                            : JSON.stringify(log.response_data, null, 2)}
                        </pre>
                        <button 
                          onClick={() => {
                            const responseText = typeof log.response_data === 'string' 
                              ? log.response_data 
                              : JSON.stringify(log.response_data, null, 2);
                            copyToClipboard(responseText);
                          }}
                          className="btn-copy"
                        >
                          Copy
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="data-section">
                          <h4>Request Data</h4>
                          <pre className="json-view">
                            {JSON.stringify(log.request_data, null, 2)}
                          </pre>
                          <button 
                            onClick={() => copyToClipboard(JSON.stringify(log.request_data, null, 2))}
                            className="btn-copy"
                          >
                            Copy
                          </button>
                        </div>
                        <div className="data-section">
                          <h4>Response Data</h4>
                          <pre className="json-view">
                            {JSON.stringify(log.response_data, null, 2)}
                          </pre>
                          <button 
                            onClick={() => copyToClipboard(JSON.stringify(log.response_data, null, 2))}
                            className="btn-copy"
                          >
                            Copy
                          </button>
                        </div>
                      </>
                    )}
                    {log.error_message && (
                      <div className="data-section error-section">
                        <h4>Error Message</h4>
                        <pre className="error-view">{log.error_message}</pre>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ApiLogs;
